import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendUp,
  TrendDown,
  CurrencyInr,
  Package,
  Users,
  Clock,
  ArrowRight,
  Package as PackageIcon,
  UserPlus,
  Calendar,
  CalendarBlank,
  Export,
  FileCsv,
  FileXls,
  FilePdf,
  ArrowsClockwise,
  Printer,
  ChatCircleDots
} from '@phosphor-icons/react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { Header } from '../../components/layout';
import { dashboardService } from '../../services';
import './Dashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const dateFilters = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
];

export function Dashboard({ onMenuClick }) {
  const [activeFilter, setActiveFilter] = useState(7);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [orderStatus, setOrderStatus] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const exportRef = useRef(null);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  const getDateRange = useCallback(() => {
    const endDate = new Date();
    const startDate = new Date();

    if (activeFilter === 'custom') {
      return {
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate
      };
    }

    startDate.setDate(endDate.getDate() - activeFilter + 1);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  }, [activeFilter, customDateRange]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const { startDate, endDate } = getDateRange();
      const data = await dashboardService.getStats(startDate, endDate);

      // Map API response to component state
      setStats({
        revenue: {
          value: data.totalRevenue?.value || 0,
          change: data.totalRevenue?.change || 0,
          period: 'vs last period'
        },
        orderValue: {
          value: data.pendingRevenue?.value || 0,
          change: data.pendingRevenue?.change || 0,
          period: 'vs last period'
        },
        totalOrders: {
          value: data.totalOrders?.value || 0,
          change: data.totalOrders?.change || 0,
          period: 'vs last period'
        },
        activeCustomers: {
          value: data.totalActiveCustomers?.value || 0,
          change: data.totalActiveCustomers?.change || 0,
          period: 'vs last period'
        },
      });

      // Map order status breakdown
      const statusMap = {};
      if (data.liveOrderStatusBreakdown) {
        data.liveOrderStatusBreakdown.forEach(item => {
          statusMap[item._id] = item.count;
        });
      }
      setOrderStatus({
        'New': statusMap['New'] || 0,
        'Pick-up Pending': statusMap['Pick-up Pending'] || 0,
        'In-Progress': statusMap['In-Progress'] || 0,
        'Delivery Pending': statusMap['Delivery Pending'] || 0,
      });

      // Map recent orders
      if (data.recentOrders) {
        setRecentOrders(data.recentOrders.map(order => ({
          id: order.orderId || order._id,
          customer: order.customerID?.customerName || 'Unknown',
          service: order.items?.[0]?.serviceCategoryID?.categoryName || 'N/A',
          amount: order.billAmount || 0,
          status: order.orderStatus
        })));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set empty data on error
      setStats({
        revenue: { value: 0, change: 0, period: 'vs last period' },
        orderValue: { value: 0, change: 0, period: 'vs last period' },
        totalOrders: { value: 0, change: 0, period: 'vs last period' },
        activeCustomers: { value: 0, change: 0, period: 'vs last period' },
      });
      setOrderStatus({});
      setRecentOrders([]);
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleCustomDateApply = () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      setActiveFilter('custom');
      setShowCustomDate(false);
    }
  };

  const handlePresetFilter = (days) => {
    setActiveFilter(days);
    setShowCustomDate(false);
    setCustomDateRange({ startDate: '', endDate: '' });
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleExport = (format) => {
    const data = {
      period: activeFilter === 'custom'
        ? `${customDateRange.startDate} to ${customDateRange.endDate}`
        : `Last ${activeFilter} days`,
      stats: stats,
      orderStatus: orderStatus,
      recentOrders: recentOrders,
      exportedAt: new Date().toLocaleString()
    };

    if (format === 'csv') {
      const csvContent = `Dashboard Report\nPeriod,${data.period}\nExported At,${data.exportedAt}\n\nMetrics\nRevenue,${stats?.revenue?.value || 0}\nOrder Value,${stats?.orderValue?.value || 0}\nTotal Orders,${stats?.totalOrders?.value || 0}\nActive Customers,${stats?.activeCustomers?.value || 0}\n\nOrder Status\n${Object.entries(orderStatus).map(([k, v]) => `${k},${v}`).join('\n')}`;
      downloadFile(csvContent, 'dashboard-report.csv', 'text/csv');
    } else if (format === 'json') {
      downloadFile(JSON.stringify(data, null, 2), 'dashboard-report.json', 'application/json');
    } else if (format === 'pdf') {
      handlePrint();
    }
    setShowExportMenu(false);
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const chartData = {
    labels: Object.keys(orderStatus),
    datasets: [
      {
        data: Object.values(orderStatus),
        backgroundColor: [
          '#8b5cf6',  // Purple - New
          '#f97316',  // Orange - Pick-up Pending
          '#3b82f6',  // Blue - In-Progress
          '#eab308',  // Yellow - Delivery Pending
        ],
        borderWidth: 3,
        borderColor: '#ffffff',
        cutout: '65%',
        hoverOffset: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // The custom .status-legend list below the chart already shows this
      // info — Chart.js's own legend just eats space the donut needs,
      // especially once .chart-container shrinks to 140px on mobile.
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleFont: { family: 'DM Sans', weight: '600' },
        bodyFont: { family: 'DM Sans' },
        padding: 12,
        cornerRadius: 8,
      },
    },
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'New': 'badge-purple',
      'Pick-up Pending': 'badge-orange',
      'In-Progress': 'badge-info',
      'Delivery Pending': 'badge-warning',
      'Delivered': 'badge-success',
    };
    return classes[status] || 'badge-neutral';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const totalLiveOrders = Object.values(orderStatus).reduce((a, b) => a + b, 0);

  return (
    <>
      <Header
        title="Dashboard"
        subtitle="Real-time insights into your laundry operations"
        onMenuClick={onMenuClick}
        actions={
          <div className="dashboard-actions">
            <button
              type="button"
              className="header-icon-btn"
              onClick={handleRefresh}
              title="Refresh"
              aria-label="Refresh dashboard"
              disabled={loading}
            >
              <span className={loading ? 'spin' : undefined} style={{ display: 'inline-flex' }}>
                <ArrowsClockwise size={18} />
              </span>
            </button>
            <button
              type="button"
              className="header-icon-btn"
              onClick={handlePrint}
              title="Print"
              aria-label="Print dashboard"
            >
              <Printer size={18} />
            </button>
            <div className="export-wrapper" ref={exportRef}>
              <button
                type="button"
                className="header-icon-btn"
                onClick={() => setShowExportMenu(!showExportMenu)}
                title="Export"
                aria-label="Export dashboard"
                aria-expanded={showExportMenu}
                aria-haspopup="menu"
              >
                <Export size={18} />
              </button>
              {showExportMenu && (
                <div className="export-dropdown" role="menu">
                  <button type="button" role="menuitem" onClick={() => handleExport('csv')}>
                    <FileCsv size={18} />
                    <span>Export as CSV</span>
                  </button>
                  <button type="button" role="menuitem" onClick={() => handleExport('json')}>
                    <FileXls size={18} />
                    <span>Export as JSON</span>
                  </button>
                  <button type="button" role="menuitem" onClick={() => handleExport('pdf')}>
                    <FilePdf size={18} />
                    <span>Print / PDF</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />
      <div className="page-content">
        <div className="dashboard-header">
          <div>
            <h2 className="section-title">Operations Overview</h2>
            <p className="section-subtitle">Snapshot for the selected period</p>
          </div>
        </div>

        <div className="filter-bar">
          <div className="date-filter-group">
            {dateFilters.map((filter) => (
              <button
                key={filter.days}
                className={`filter-btn ${activeFilter === filter.days ? 'active' : ''}`}
                onClick={() => handlePresetFilter(filter.days)}
              >
                {filter.label}
              </button>
            ))}
            <div className="custom-date-wrapper">
              <button
                className={`filter-btn custom-date-btn ${activeFilter === 'custom' ? 'active' : ''}`}
                onClick={() => setShowCustomDate(!showCustomDate)}
              >
                <CalendarBlank size={16} />
                {activeFilter === 'custom'
                  ? `${customDateRange.startDate} - ${customDateRange.endDate}`
                  : 'Custom'}
              </button>
              {showCustomDate && (
                <div className="custom-date-dropdown">
                  <div className="date-input-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={customDateRange.startDate}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="date-input-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={customDateRange.endDate}
                      onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                  <button className="apply-date-btn" onClick={handleCustomDateApply}>
                    Apply
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner" />
            <span style={{ marginTop: 12 }}>Loading dashboard...</span>
          </div>
        ) : (
          <>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon revenue">
                  <CurrencyInr size={24} />
                </div>
                <div className="stat-value">{formatCurrency(stats.revenue.value)}</div>
                <div className="stat-label">Revenue Received</div>
                <div className={`stat-change ${stats.revenue.change >= 0 ? 'increase' : 'decrease'}`}>
                  {stats.revenue.change >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                  <span>{Math.abs(stats.revenue.change).toFixed(1)}%</span>
                  <span className="stat-period">{stats.revenue.period}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon pending">
                  <Clock size={24} />
                </div>
                <div className="stat-value">{formatCurrency(stats.orderValue.value)}</div>
                <div className="stat-label">Order Value</div>
                <div className={`stat-change ${stats.orderValue.change >= 0 ? 'increase' : 'decrease'}`}>
                  {stats.orderValue.change >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                  <span>{Math.abs(stats.orderValue.change).toFixed(1)}%</span>
                  <span className="stat-period">{stats.orderValue.period}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon orders">
                  <Package size={24} />
                </div>
                <div className="stat-value">{stats.totalOrders.value}</div>
                <div className="stat-label">Total Orders</div>
                <div className={`stat-change ${stats.totalOrders.change >= 0 ? 'increase' : 'decrease'}`}>
                  {stats.totalOrders.change >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                  <span>{Math.abs(stats.totalOrders.change).toFixed(1)}%</span>
                  <span className="stat-period">{stats.totalOrders.period}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon customers">
                  <Users size={24} />
                </div>
                <div className="stat-value">{stats.activeCustomers.value}</div>
                <div className="stat-label">Active Customers</div>
                <div className={`stat-change ${stats.activeCustomers.change >= 0 ? 'increase' : 'decrease'}`}>
                  {stats.activeCustomers.change >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                  <span>{Math.abs(stats.activeCustomers.change).toFixed(1)}%</span>
                  <span className="stat-period">{stats.activeCustomers.period}</span>
                </div>
              </div>
            </div>

            <div className="dashboard-grid">
              <div className="dashboard-section card">
                <div className="section-header">
                  <h3>Live Order Status</h3>
                  <span className="live-order-count">Total: {totalLiveOrders}</span>
                </div>
                <div className="chart-container">
                  <Doughnut data={chartData} options={chartOptions} />
                </div>
                <div className="status-legend">
                  {Object.entries(orderStatus).map(([status, count]) => (
                    <div key={status} className="status-legend-item">
                      <span className={`status-dot ${status.toLowerCase().replace(/\s+/g, '-')}`}></span>
                      <span className="status-label">{status}</span>
                      <span className="status-count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="dashboard-section card">
                <div className="section-header">
                  <h3>Recent Orders</h3>
                  <Link to="/orders" className="view-all-link">
                    View All <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="recent-orders-list">
                  {recentOrders.length > 0 ? recentOrders.map((order) => (
                    <div key={order.id} className="order-item">
                      <div className="order-info">
                        <span className="order-id">{order.id}</span>
                        <span className="order-customer">{order.customer}</span>
                      </div>
                      <div className="order-service">{order.service}</div>
                      <div className="order-amount">{formatCurrency(order.amount)}</div>
                      <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                  )) : (
                    <div className="empty-orders">No recent orders</div>
                  )}
                </div>
              </div>
            </div>

            <div className="quick-actions-section">
              <h3>Quick Actions</h3>
              <div className="actions-grid">
                <Link to="/orders" className="action-card">
                  <div className="action-icon orders">
                    <PackageIcon size={24} />
                  </div>
                  <div className="action-content">
                    <span className="action-title">Manage Orders</span>
                    <span className="action-description">View, update, and track all orders</span>
                  </div>
                  <ArrowRight size={20} className="action-arrow" />
                </Link>

                <Link to="/customers" className="action-card highlight">
                  <div className="action-icon customers">
                    <UserPlus size={24} />
                  </div>
                  <div className="action-content">
                    <span className="action-title">Add Customer</span>
                    <span className="action-description">Create new customer profiles and bookings</span>
                  </div>
                  <ArrowRight size={20} className="action-arrow" />
                </Link>

                <Link to="/slots" className="action-card">
                  <div className="action-icon slots">
                    <Calendar size={24} />
                  </div>
                  <div className="action-content">
                    <span className="action-title">Configure Slots</span>
                    <span className="action-description">Manage pickup and delivery time slots</span>
                  </div>
                  <ArrowRight size={20} className="action-arrow" />
                </Link>

                <Link to="/whatsapp?tab=templates&create=1" className="action-card">
                  <div className="action-icon whatsapp">
                    <ChatCircleDots size={24} />
                  </div>
                  <div className="action-content">
                    <span className="action-title">Create WhatsApp Template</span>
                    <span className="action-description">Submit a new message template for Meta approval</span>
                  </div>
                  <ArrowRight size={20} className="action-arrow" />
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default Dashboard;
